<template>
  <div class="wm_section">
    <div
      ref="bar"
      class="wm_section_bar"
      :class="{ wm_section_bar_sticky: sticky, wm_section_bar_stuck: stuck }"
      :style="bar_style"
    >
      <SectionEnd :class="bar_border_class"/>
      <SectionBegin
        class="wm_section_head"
        :class="bar_color_class"
        :bar_text_class="bar_text_class"
        :title="this.title"
        v-model="collapsed"
      />
      <SectionEnd :class="bar_border_class"/>
    </div>
    <div
      v-if="!collapsed"
      :class="{ wm_section_content: content_padding }"
      :style="content_style"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

const STACK_KEY = "wm_section_sticky_stack";
const DEPTH_KEY = "wm_section_sticky_depth";
const LAYOUT_CHANGED_EVENT = "wm-layout-changed";

/* The scroll container a sticky bar is pinned against. */
function scrolling_ancestor(el) {
  let parent = el.parentElement;
  while (parent) {
    const overflow_y = window.getComputedStyle(parent).overflowY;
    if (overflow_y === "auto" || overflow_y === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export default {
  // eslint-disable-next-line
  name: "Section",

  inject: {
    ancestor_sticky_stack: { from: STACK_KEY, default: 0 },
    ancestor_sticky_depth: { from: DEPTH_KEY, default: 0 }
  },

  provide() {
    return {
      [STACK_KEY]: computed(() => this.sticky_stack),
      [DEPTH_KEY]: computed(() => this.sticky_depth)
    };
  },

  data() {
    return {
      collapsed: false,
      bar_height: 0,
      stuck: false
    };
  },

  created() {
    this.collapsed = this.collapsedFromStart;
  },

  mounted() {
    if (!this.sticky) {
      return;
    }
    this.measure_bar();

    if (typeof ResizeObserver !== "undefined") {
      this.bar_observer = new ResizeObserver(() => this.measure_bar());
      this.bar_observer.observe(this.$refs.bar);
    }

    this.$nextTick(() => this.bind_stuck_observer());
    window.addEventListener("resize", this.bind_stuck_observer);
  },

  beforeUnmount() {
    if (this.bar_observer) {
      this.bar_observer.disconnect();
      this.bar_observer = null;
    }
    if (this.stuck_observer) {
      this.stuck_observer.disconnect();
      this.stuck_observer = null;
    }
    window.removeEventListener("resize", this.bind_stuck_observer);
  },

  emits: ['collapsed-change'],

  watch: {
    ancestor_sticky_stack() {
      if (this.sticky) {
        this.$nextTick(() => this.bind_stuck_observer());
      }
    },

    collapsedFromStart(newVal) {
      this.collapsed = newVal;
    },
    collapsed(newVal) {
      this.$emit('collapsed-change', newVal);
      this.$nextTick(() => window.dispatchEvent(new Event(LAYOUT_CHANGED_EVENT)));
    }
  },

  props: {
    title: {
      type: String,
      default: "Unnamed section"
    },
    content_padding: {
      type: Boolean,
      default: true
    },
    collapsedFromStart: {
      type: Boolean,
      default: false
    },
    sticky: {
      type: Boolean,
      default: false
    },
    bar_color_class: {
      type: String,
      default: ""
    },
    bar_text_class: {
      type: String,
      default: ""
    },
    bar_border_class: {
      type: String,
      default: ""
    },
  },

  computed: {
    sticky_stack() {
      return this.ancestor_sticky_stack + (this.sticky ? this.bar_height : 0);
    },

    sticky_depth() {
      return this.ancestor_sticky_depth + (this.sticky ? 1 : 0);
    },

    bar_style() {
      if (!this.sticky) {
        return null;
      }

      const overlap = this.ancestor_sticky_stack > 0 ? 1 : 0;

      return {
        top: `calc(var(--wm_sticky_offset, 0px) - var(--wm_sticky_pad, 0px) + ${this.ancestor_sticky_stack - overlap}px)`,
        zIndex: Math.max(3, 9 - this.ancestor_sticky_depth)
      };
    },

    content_style() {
      if (this.sticky_stack === 0) {
        return null;
      }
      return { "--wm_section_stack": `${this.sticky_stack}px` };
    }
  },

  methods: {
    bind_stuck_observer() {
      if (this.stuck_observer) {
        this.stuck_observer.disconnect();
        this.stuck_observer = null;
      }

      const bar = this.$refs.bar;
      if (!bar || typeof IntersectionObserver === "undefined") {
        return;
      }

      const rest_top = parseFloat(window.getComputedStyle(bar).top) || 0;

      this.stuck_observer = new IntersectionObserver(
        entries => {
          this.stuck = entries[entries.length - 1].intersectionRatio < 1;
        },
        {
          root: scrolling_ancestor(bar),
          rootMargin: `-${rest_top + 1}px 0px 9999px 0px`,
          threshold: [1]
        }
      );
      this.stuck_observer.observe(bar);
    },

    measure_bar() {
      const el = this.$refs.bar;
      if (el) {
        this.bar_height = el.getBoundingClientRect().height;
      }
    },

    on_collapse(event) {
      this.collapsed = event;
    }
  }
};
</script>
<style lang="less">
.open-enter-active {
  transition: all 0.05s ease;
}

.open-enter,
.open-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
